import PromosService from '../../services/PromosService';
import { mapPromos } from './promos.utils';
import { PromosState, Promotion } from './promos.types';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
  injectSlice,
  appLogout,
  State,
  AuthenticationStatus,
  createAction,
  selectMarketingMeta,
} from '@betty/toolkit/state';

export const getPromos = createAction('promos/get');

export const fetchPromos = createAsyncThunk<Promotion[], AuthenticationStatus>(
  'promos/fetch',
  async (authenticationStatus, { getState }) => {
    if (authenticationStatus === 'authenticated') {
      const promos = await PromosService.getPromos();

      return mapPromos(promos.items);
    }

    const marketingMeta = selectMarketingMeta(getState() as State);
    const publicPromos = await PromosService.getPublicPromos({
      params: {
        ReferralCode: marketingMeta.referral_code,
        UtmCampaign: marketingMeta.utm_campaign,
      },
    });

    return mapPromos(publicPromos.items);
  },
);

export const optIn = createAsyncThunk(
  'promos/user/optIn',
  async (id: Promotion['promotionId']) => {
    const res = await PromosService.optIn({ pathParams: { promoId: id } });

    return res;
  },
);

export const optOut = createAsyncThunk(
  'promos/user/optOut',
  async (id: Promotion['promotionId']) => {
    const res = await PromosService.optOut({ pathParams: { promoId: id } });

    return res;
  },
);

const initialState: PromosState = {
  authenticated: {
    data: [],
    dataState: 'idle',
    optInPromoIds: [],
    optOutPromoIds: [],
  },
  unauthenticated: {
    data: [],
    dataState: 'idle',
  },
};

const promos = createSlice({
  name: 'promos',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPromos.pending, (state, { meta }) => {
      state[meta.arg].dataState = 'pending';
    });

    builder.addCase(fetchPromos.fulfilled, (state, { payload, meta }) => {
      state[meta.arg].dataState = 'fulfilled';
      state[meta.arg].data = payload;
    });

    builder.addCase(fetchPromos.rejected, (state, { meta }) => {
      state[meta.arg].dataState = 'rejected';
    });

    builder.addCase(optIn.pending, (state, action) => {
      state.authenticated.optInPromoIds.push(action.meta.arg);
    });
    builder.addCase(optOut.pending, (state, action) => {
      state.authenticated.optOutPromoIds.push(action.meta.arg);
    });
    builder.addCase(optOut.fulfilled, (state, action) => {
      const index = state.authenticated.data.findIndex(
        (promo) => promo.promotionId === action.meta.arg,
      );

      if (state.authenticated.data[index]) {
        state.authenticated.data[index].isOptedIn = false;
        state.authenticated.data[index].status = 'Available';
      }
    });
    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addMatcher(optIn.settled, (state, action) => {
      state.authenticated.optInPromoIds =
        state.authenticated.optInPromoIds.filter(
          (id) => id !== action.meta.arg,
        );
    });
    builder.addMatcher(optOut.settled, (state, action) => {
      state.authenticated.optOutPromoIds =
        state.authenticated.optOutPromoIds.filter(
          (id) => id !== action.meta.arg,
        );
    });
  },
});

export const withPromos = injectSlice({
  reducerPath: 'promos',
  reducer: promos.reducer,
});

export const selectPromos = withPromos.selector(
  (state) => state.promos[state.app.authenticationStatus].data,
);

export const selectPromo = createSelector(
  [(_, id: Promotion['promotionId'] | Promotion['slug']) => id, selectPromos],
  (id, promos) =>
    promos.find((promo) => promo.slug === id || promo.promotionId == id),
);
export const selectPromosDataState = withPromos.selector(
  (state) => state.promos[state.app.authenticationStatus].dataState,
);

export const selectPromosByType = (type: Promotion['promoType']) =>
  createSelector(selectPromos, (promos) =>
    promos.filter((promo) => promo.promoType === type),
  );

export const selectOptInPending =
  (id: Promotion['promotionId']) => (state: State) =>
    state.promos?.authenticated.optInPromoIds.indexOf(id) !== -1;

export const selectOptOutPending =
  (id: Promotion['promotionId']) => (state: State) =>
    state.promos?.authenticated.optOutPromoIds.indexOf(id) !== -1;

export const selectAnotherOptedInPromoByType = (
  id: Promotion['promotionId'],
  type: Promotion['promoType'],
) =>
  createSelector(selectPromosByType(type), (promos) =>
    promos.find((promo) => promo.isOptedIn && promo.promotionId !== id),
  );

export const selectReferrerPromo = createSelector(selectPromos, (promos) =>
  promos.find((promo) => promo.contentType === 'Referrer'),
);

declare global {
  interface LazyLoadedSlices {
    promos: ReturnType<typeof promos.reducer>;
  }
}
